<template>
  <bm-dialog
    v-if="!showScanContainer"
    ref="assemblyFillingDialogContainer"
    :label="$m.production_packaging_assembly_fill_containers"
    :width="700"
    @accept="completeFilling"
    @cancel="cancelFilling"
  >
    <!-- We want to use the dialog's valid state elsewhere -->
    <template #default="{ valid }">
      <v-container fluid pa-0>
        <v-row>
          <v-col v-if="!isAssembly" :cols="12" class="pb-0">
            <bm-alert type="warning" dismissible>{{
              $m.racking_non_assembly_warning
            }}</bm-alert>
          </v-col>
          <v-col :cols="12">
            <bm-dropdown
              :value.sync="selectedOutputStockItemId"
              :t="$m.keys.racking_output_product"
              :items="outputStockItems"
              :item-text="itemExtracts.nameCode"
              :item-value="itemExtracts.id"
              :item-hidden="itemExtracts.isHidden"
              :read-only="readOnly"
              :rules="[$ruleSet.required()]"
              :disabled="isAssembly"
            />
          </v-col>
          <v-col cols="12">
            <bm-text
              :value.sync="urn"
              :t="$m.keys.assembly_batch_number"
              :rules="[$ruleSet.required()]"
              :disabled="isAssembly"
            />
          </v-col>
        </v-row>
        <template v-if="!readOnly">
          <v-row>
            <v-col cols="12">
              <bm-btn
                button-type="secondary"
                block
                :t="$m.keys.racking_scan_containers"
                @click="showScanContainer = true"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="8">
              <bm-barcode-input
                ref="barcodeText"
                :value.sync="currentPermLabel"
                :rules="[$ruleSet.required()]"
                :barcode-types="[EnumBrewmanBarcodeType.tracking]"
                :t="$m.keys.racking_enter_label_no_label"
                :locked-mode="BmBarcodeTextMode.Text"
              />
            </v-col>
            <v-col cols="auto" align-self="center">
              <bm-btn
                button-type="primary"
                :disabled="!valid"
                :t="$m.keys.add"
                @click="addContainerAndClearFields(currentPermLabel)"
              />
            </v-col>
          </v-row>
        </template>
        <v-row class="pt-0 mt-0">
          <v-col :cols="12">
            <p class="scanned-containers">{{ scannedContainersText }}</p>
            <p class="scanned-containers">{{ scannedContainersSummary }}</p>
          </v-col>
          <v-col v-if="errorMessage" :cols="12">
            <bm-alert type="warning">{{ errorMessage }}</bm-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" :sm="showSplitGrid ? 6 : 12">
            <bm-grid
              :items="leftGridItems"
              :grid-configuration="gridConfiguration"
              :allow-card-view="false"
              :read-only="readOnly"
              :row-classes="(row) => getClassForRow(row.item)"
            />
          </v-col>
          <v-col v-if="showSplitGrid" :cols="6">
            <bm-grid
              :items="rightGridItems"
              :grid-configuration="gridConfiguration"
              :allow-card-view="false"
              :read-only="readOnly"
              :row-classes="(row) => getClassForRow(row.item)"
            />
          </v-col>
        </v-row>
      </v-container>
    </template>

    <template #controls-confirm>
      <bm-btn
        :block="isDeviceView"
        :t="$m.keys.finish"
        @click="completeFilling"
      />
    </template>
  </bm-dialog>
  <scan-containers-dialog
    v-else
    :dialog-title="$m.racking_scan_container"
    @input-complete="addContainerAndClearFields"
    @cancel="showScanContainer = false"
  >
    <template #additional-information>
      <p class="scanned-containers">{{ scannedContainersText }}</p>
      <bm-grid
        :items="currentSessionPermLabels"
        :grid-configuration="gridConfiguration"
        :allow-card-view="false"
        :read-only="readOnly"
        :row-classes="(row) => getClassForRow(row.item)"
      />
    </template>
  </scan-containers-dialog>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmDialog from "@/components/dialogs/BmDialog.vue";
import BmAlert from "@/components/controls/BmAlert.vue";
import BmBarcodeInput from "@/components/controls/device/BmBarcodeInput.vue";
import { BmBarcodeTextMode } from "@/components/controls/device/barcodeUtils";
import BmGrid from "@/components/controls/grid/BmGrid.vue";
import type { ContainerWithStatus } from "@/app/services/generated/containerService";
import GridConfigurationBuilder from "@/components/controls/grid/builder/GridConfigurationBuilder";
import ScanContainersDialog from "@/components/pages/productionv1/assemblies/dialogs/ScanContainersDialog.vue";
import { EnumBrewmanBarcodeType } from "@/app/services/generated/enums/enumBrewmanBarcodeType.generated";
import { computed, onMounted, ref, shallowRef, watch } from "vue";
import messages, { keys } from "@/plugins/i18n";
import type { DialogEvents } from "@/components/mixins/dialogs";
import { useReactiveDialogScriptSetup } from "@/components/mixins/dialogs";
import { filterNonUniqueByProperty } from "@/app/apiutils";
import {
  makeRef,
  withLoading,
  wrapLoading,
} from "@/components/composables/componentTools";
import type Guid from "@/app/types/common/guid";
import type { FillContainersEvent } from "@/components/pages/containers/composables/fillContainers";
import { useLeaveDialog } from "@/components/mixins/BmLeaveConfirmation";
import PackageTypesCache from "@/app/caching/caches/packageTypesCache";
import apiCache from "@/app/caching/apiCache";
import { showInformationDialog } from "@/components/composables/promptDialogs";
import { EnumDialogType } from "@/components/dialogs/DialogTypes";
import type { ScannedContainerItem } from "@/components/pages/containers/composables/scanContainers";
import { useScanContainers } from "@/components/pages/containers/composables/scanContainers";
import type { Item } from "@/app/services/generated/itemService";
import { ItemFilter, itemService } from "@/app/services/generated/itemService";
import { EnumItemMainPurpose } from "@/app/services/generated/enums/enumItemMainPurpose.generated";
import type { BmFormInterface } from "@/components/controls/BmFormTypes";
import { screenSize } from "@/vue23switching/screenSize";
import { itemExtracts } from "@/business_logic/stock/itemUtils";

export interface FillContainersDialogProps {
  filledContainers?: ContainerWithStatus[];
  readOnly?: boolean;
  stockItemId?: Guid;
  batchUrn?: string;
  isAssembly?: boolean;
}

export interface FillContainersDialogEvents extends DialogEvents<FillContainersEvent> {}

const props = withDefaults(defineProps<FillContainersDialogProps>(), {
  filledContainers: () => [],
  readOnly: false,
  isAssembly: false,
});
const emit = defineEmits<FillContainersDialogEvents>();

const { accept, cancel } = useReactiveDialogScriptSetup(emit);
const {
  currentSessionPermLabels,
  scannedContainersText,
  scannedContainersSummary,
  addContainer,
  removeContainer,
  validateScanning,
  scannedContainers: permLabelsToAdd,
} = useScanContainers();

const { checkUserCanLeave } = useLeaveDialog(
  computed(
    () =>
      !!(
        permLabelsToAdd.value.filter((x) => x.scannedThisSession).length ||
        filledContainers.value.filter((x) =>
          permLabelsToAdd.value.every(
            (y) => y.permLabel !== x.container.permLabel
          )
        ).length
      )
  )
);

const cache = apiCache.buildCacheSet({
  packageTypes: PackageTypesCache,
});

const currentPermLabel = ref("");
const showScanContainer = ref(false);
const splitCount = ref(10);
const filledContainers = makeRef(props.filledContainers);

const urn = makeRef(props.batchUrn);
const selectedOutputStockItemId = makeRef(props.stockItemId);
const outputStockItems = shallowRef<readonly Item[]>([]);

// refs
const assemblyFillingDialogContainer = ref<BmFormInterface | null>(null);
const barcodeText = ref<any>(null);

const isDeviceView = computed(() => {
  return screenSize.xs;
});

/**
 * Added item appear at the top of the list, initially 10 to a col, once 20 are in can the items try to appear evenly
 * so if there's 21 the col1 would have 11 and col2 would have 10, 22 col1 would have 11 and col 2 would have 11
 */
const showSplitGrid = computed(
  () => screenSize.smAndUp && permLabelsToAdd.value.length > splitCount.value
);

const leftGridItems = computed(() => {
  return showSplitGrid.value
    ? [
        ...permLabelsToAdd.value.slice(0, 10),
        ...permLabelsToAdd.value.slice(20).filter((_, idx) => idx % 2 == 0),
      ]
    : permLabelsToAdd.value;
});

const rightGridItems = computed(() => {
  return [
    ...permLabelsToAdd.value.slice(10, 20),
    ...permLabelsToAdd.value.slice(20).filter((_, idx) => idx % 2 == 1),
  ];
});

const gridConfiguration = computed(() => {
  let maker = new GridConfigurationBuilder<ScannedContainerItem>({
    itemId: (item) => item.permLabel,
  });

  maker.addTextColumn(keys.containers_perm_label, (item) => item.permLabel);

  if (permLabelsToAdd.value.some((item) => item.errorMessage)) {
    maker
      .addTextColumn(keys.container_error_message, (item) => item.errorMessage)
      .withTextTruncation();
  }

  if (!props.readOnly) {
    maker
      .addActionsColumn()
      .withAction(
        messages.racking_empty,
        (item) => removeContainer(item.permLabel),
        (item) => !item.scannedThisSession
      )
      .withAction(
        messages.racking_remove,
        (item) => removeContainer(item.permLabel),
        (item) => item.scannedThisSession
      );
  }

  return maker.build();
});

watch(
  filledContainers,
  () => {
    // Update the already filled containers
    permLabelsToAdd.value = filledContainers.value.map((c) => ({
      permLabel: c.container.permLabel,
      scannedThisSession: false,
      errorMessage: null,
      hasBeenValidated: true,
      palletLabelAssociated: null,
    }));

    permLabelsToAdd.value = filterNonUniqueByProperty(
      permLabelsToAdd.value,
      (x) => x.permLabel
    );
  },
  { immediate: true }
);

async function completeFilling() {
  // Emit to our parent the containers to add or remove
  if (!(await wrapLoading(validateFilling()))) {
    await showInformationDialog(
      messages.racking_fill_dialog_error_warning_title,
      messages.racking_fill_dialog_error_warning,
      { type: EnumDialogType.warning }
    );
    return;
  }

  const toAdd = permLabelsToAdd.value
    .filter((x) => x.scannedThisSession)
    .map((x) => x.permLabel);

  // Empty the ones that are already filled but since removed
  const toRemove = filledContainers.value
    .filter((x) =>
      permLabelsToAdd.value.every((y) => y.permLabel !== x.container.permLabel)
    )
    .map((x) => x.container.permLabel);

  accept({
    urn: urn.value,
    stockItemId: selectedOutputStockItemId.value,
    permLabelsToAdd: toAdd,
    permLabelsToRemove: toRemove,
  } as FillContainersEvent);

  // Clear down values and reset
  permLabelsToAdd.value = [];
  assemblyFillingDialogContainer.value?.resetValidation();
}

const errorMessage = computed(() => {
  if (permLabelsToAdd.value.length === 0) {
    return null;
  }
  if (permLabelsToAdd.value.some((x) => x.errorMessage)) {
    return messages.containers_fill_error;
  }
  if (!urn.value) {
    return messages.racking_no_urn_warning;
  }
  if (!selectedOutputStockItemId.value) {
    return messages.racking_no_stock_item_warning;
  }
  return null;
});

async function addContainerAndClearFields(label: string) {
  await addContainer(label);

  currentPermLabel.value = "";
  assemblyFillingDialogContainer.value?.resetValidation();
}

async function validateFilling(containerItem?: ScannedContainerItem) {
  return (await validateScanning(containerItem)) && errorMessage.value === null;
}

function getClassForRow(item: ScannedContainerItem) {
  if (item.errorMessage !== null) {
    return ["grid-row-error"];
  } else if (!item.scannedThisSession || item.hasBeenValidated) {
    return ["grid-row-success"];
  } else {
    return [];
  }
}

const loadPrerequisites = withLoading(async () => {
  const stockItemFilter = new ItemFilter();
  stockItemFilter.mainPurposes = [
    EnumItemMainPurpose.product,
    EnumItemMainPurpose.autoAssembledItem,
  ];

  const [outputStockItemsSummaries] = await Promise.all([
    itemService.getItemsByFilter(stockItemFilter),

    cache.update(),
  ]);

  outputStockItems.value = Object.freeze(
    outputStockItemsSummaries.items.filter(
      (x) =>
        x.packaging.packageTypeId &&
        cache.packageTypes.getById(x.packaging.packageTypeId)?.isReturnable
    )
  );
});

onMounted(loadPrerequisites);

async function cancelFilling() {
  if (await checkUserCanLeave()) {
    cancel();
  }
}
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

:deep(.full-width)
  width: 100%

.scanned-containers
  @include bm-body2
  color: $bm-color-brand01
</style>
